import { createContext, useContext, useEffect, useState, useCallback } from "react";

// Tema: "light" | "dark" | "system"
// Persistido em localStorage. "system" respeita prefers-color-scheme.
const STORAGE_KEY = "instituto-cuidar:theme";
const ThemeContext = createContext(null);

function getSystemPreference() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyThemeClass(effective) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.style.colorScheme = effective;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "system";
    return localStorage.getItem(STORAGE_KEY) || "system";
  });
  const [effective, setEffective] = useState(() =>
    (theme === "system" ? getSystemPreference() : theme)
  );

  useEffect(() => {
    const eff = theme === "system" ? getSystemPreference() : theme;
    setEffective(eff);
    applyThemeClass(eff);
  }, [theme]);

  // Se o sistema mudar de tema e estamos em "system", segue automaticamente.
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const eff = getSystemPreference();
      setEffective(eff);
      applyThemeClass(eff);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const toggle = useCallback(() => {
    // Toggle simples entre light e dark (sem passar por system).
    setTheme(effective === "dark" ? "light" : "dark");
  }, [effective, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, effective, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
